import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BenefitCard from '../components/BenefitCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Benefits = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const benefits = [
    {
      icon: '💧',
      title: 'Flushes Toxins Naturally',
      description: 'Deep cleansing that removes accumulated toxins from your body naturally and safely.',
    },
    {
      icon: '⚙️',
      title: 'Boosts Metabolism',
      description: 'Kickstart your digestive system and enhance metabolic efficiency for better health.',
    },
    {
      icon: '🌙',
      title: 'Improves Sleep & Balance',
      description: 'Regulate your circadian rhythm and achieve hormonal balance for restful sleep.',
    },
    {
      icon: '🔥',
      title: 'Revives Energy & Vitality',
      description: 'Feel recharged with sustained energy levels throughout your day.',
    },
    {
      icon: '✨',
      title: 'Glowing Skin',
      description: 'Achieve radiant, clear skin by eliminating toxins and improving circulation.',
    },
    {
      icon: '🧘',
      title: 'Mental Clarity',
      description: 'Experience improved focus, reduced brain fog, and enhanced cognitive function.',
    },
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-accent to-green-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Benefits of Cleansing Therapy
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how a 16-hour holistic detox can transform your body and mind
          </p>
        </motion.div>

        {/* Benefits Carousel */}
        <div className="relative mb-12">
          {/* Scroll Buttons */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide px-12 py-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
                index={index}
              />
            ))}
          </div>
        </div>

        {/* Testimonials Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white rounded-3xl p-8 shadow-xl mb-8"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            What Our Clients Say
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-primary/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400">⭐</span>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Amazing experience! Felt rejuvenated after the therapy. My energy levels are through the roof!"
              </p>
              <p className="font-semibold text-gray-800">- Anjali K.</p>
            </div>
            <div className="bg-primary/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400">⭐</span>
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Best detox therapy I have tried. Highly recommend! My sleep quality has improved dramatically."
              </p>
              <p className="font-semibold text-gray-800">- Rahul M.</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/nearby')}
            className="bg-primary text-white px-12 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Find a Therapist Near Me
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Benefits;
